import { useCallback, useEffect, useState } from 'react';
import { T } from '../../components/text/TextEdit';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { api } from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Switch } from '../ui/switch';
import { SectionCard, Field, Skeleton } from '../pipeline/ui';
import { formatCredits } from '../../lib/credits';
import { ProvidersEditor } from './ProvidersEditor';

// READ-ONLY god financial overview: total balances, RAG-vs-model spend split, transactions.
// (No revenue / liability / promo panels — those are a later phase.) Accepts `data` for tests.
export function WalletDashboardView({ data, peg = 0.01 }) {
  const { t } = useTranslation();
  const split = data?.spend_split || { model: 0, rag: 0, total_balances: 0, total_reserved: 0 };
  const ledger = data?.ledger || [];
  const p = data?.credit_usd_peg ?? peg;
  return (
    <SectionCard title={t('walletCore.dashboardTitle')} testId="wallet-dashboard">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Stat label={<T k="walletCore.totalBalances" />} value={formatCredits(split.total_balances, p)} testId="wallet-total-balances" />
        <Stat label={<T k="walletCore.totalReserved" />} value={formatCredits(split.total_reserved, p)} testId="wallet-total-reserved" />
        <Stat label={<T k="walletCore.spendModel" />} value={formatCredits(split.model, p)} testId="wallet-spend-model" />
        <Stat label={<T k="walletCore.spendRag" />} value={formatCredits(split.rag, p)} testId="wallet-spend-rag" />
      </div>

      <h4 className="mt-6 mb-2 text-sm font-semibold text-foreground"><T k="walletCore.txTitle" /></h4>
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="wallet-transactions-table">
          <thead>
            <tr className="text-start text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
              <th className="text-start py-2 pe-4"><T k="walletCore.colType" /></th>
              <th className="text-start py-2 pe-4"><T k="walletCore.colDelta" /></th>
              <th className="text-start py-2 pe-4"><T k="walletCore.colBalance" /></th>
              <th className="text-start py-2 pe-4"><T k="walletCore.colReserved" /></th>
              <th className="text-start py-2 pe-4"><T k="walletCore.colRef" /></th>
              <th className="text-start py-2"><T k="walletCore.colWhen" /></th>
            </tr>
          </thead>
          <tbody className="font-data">
            {ledger.length === 0 ? (
              <tr><td colSpan="6" className="py-3 text-muted-foreground" data-testid="wallet-tx-empty">—</td></tr>
            ) : ledger.map((row) => (
              <tr key={row.id} className="border-b border-border" data-testid={`wallet-tx-${row.id}`}>
                <td className="py-2 pe-4">{row.entry_type}</td>
                <td className="py-2 pe-4">{row.credits_delta}</td>
                <td className="py-2 pe-4">{row.balance_credits_after}</td>
                <td className="py-2 pe-4">{row.reserved_credits_after}</td>
                <td className="py-2 pe-4 text-muted-foreground">{row.meta?.split ? `${row.meta.split.model}m/${row.meta.split.rag}r` : (row.ref_kind || '')}</td>
                <td className="py-2 text-muted-foreground">{row.created_at ? new Date(row.created_at).toLocaleString() : ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </SectionCard>
  );
}

function Stat({ label, value, testId }) {
  return (
    <div className="rounded-md border border-border bg-card p-3">
      <div className="text-xs uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="mt-1 text-sm font-data text-foreground" data-testid={testId}>{value}</div>
    </div>
  );
}

const EMPTY_MODEL = {
  model_id: '', provider: '', display_label: '', tier_hint: '', charge_type: 'formula',
  flat_credits: '', usd_price_per_1k_input: '', usd_price_per_1k_output: '',
  user_selectable: false, is_active: true,
};

// Full CRUD editor for the model_prices registry (super-admin). Add/edit/deactivate any model —
// no hardcoded lists. The Settings default-model selector + generation picker read the same table.
export function ModelPricesEditor({ models = [], onSave, onToggleActive }) {
  const { t } = useTranslation();
  const [form, setForm] = useState(EMPTY_MODEL);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const isFlat = form.charge_type === 'flat';

  const edit = (m) => {
    setForm({
      ...EMPTY_MODEL, ...m,
      provider: m.provider ?? '', display_label: m.display_label ?? '', tier_hint: m.tier_hint ?? '',
      flat_credits: m.flat_credits ?? '',
      usd_price_per_1k_input: m.usd_price_per_1k_input ?? '',
      usd_price_per_1k_output: m.usd_price_per_1k_output ?? '',
      is_active: m.is_active !== false,
    });
    setEditing(true);
  };
  const reset = () => { setForm(EMPTY_MODEL); setEditing(false); };

  const submit = async () => {
    if (!String(form.model_id).trim()) { toast.error(t('walletCore.mpModelRequired')); return; }
    setSaving(true);
    try { await onSave(form); toast.success(t('walletCore.mpSaved')); reset(); }
    catch (e) { toast.error(e.message); } finally { setSaving(false); }
  };

  const priceCell = (m) => (m.charge_type === 'flat'
    ? `${m.flat_credits ?? '—'} cr`
    : `$${m.usd_price_per_1k_input ?? '—'} / $${m.usd_price_per_1k_output ?? '—'}`);

  return (
    <SectionCard title={t('walletCore.pricingTitle')} testId="wallet-pricing-card">
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="model-prices-table">
          <thead>
            <tr className="text-start text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
              <th className="text-start py-2 pe-4"><T k="walletCore.colProvider" /></th>
              <th className="text-start py-2 pe-4"><T k="walletCore.colModel" /></th>
              <th className="text-start py-2 pe-4"><T k="walletCore.colType" /></th>
              <th className="text-start py-2 pe-4"><T k="walletCore.colPrice" /></th>
              <th className="text-start py-2 pe-4"><T k="walletCore.selectable" /></th>
              <th className="text-start py-2 pe-4"><T k="walletCore.colActive" /></th>
              <th className="text-start py-2"></th>
            </tr>
          </thead>
          <tbody className="font-data">
            {models.map((m) => (
              <tr key={m.model_id} className={`border-b border-border ${m.is_active === false ? 'opacity-50' : ''}`} data-testid={`model-row-${m.model_id}`}>
                <td className="py-2 pe-4 text-muted-foreground">{m.provider || '—'}</td>
                <td className="py-2 pe-4 text-foreground">
                  <div>{m.model_id}</div>
                  {m.display_label && <div className="text-xs text-muted-foreground">{m.display_label}</div>}
                </td>
                <td className="py-2 pe-4 text-muted-foreground">{m.charge_type}</td>
                <td className="py-2 pe-4">{priceCell(m)}</td>
                <td className="py-2 pe-4">
                  <Switch checked={!!m.user_selectable} onCheckedChange={(v) => onSave({ ...m, user_selectable: v })} data-testid={`price-selectable-${m.model_id}`} />
                </td>
                <td className="py-2 pe-4">
                  <span className={`inline-flex px-2 py-0.5 rounded-full text-xs border ${m.is_active === false ? 'bg-muted text-muted-foreground border-border' : 'bg-success-soft text-success border-success-border'}`} data-testid={`model-active-${m.model_id}`}>
                    {m.is_active === false ? t('walletCore.inactive') : t('walletCore.active')}
                  </span>
                </td>
                <td className="py-2 text-end whitespace-nowrap">
                  <Button variant="ghost" size="sm" onClick={() => edit(m)} data-testid={`model-edit-${m.model_id}`}><T k="walletCore.edit" /></Button>
                  <Button variant="ghost" size="sm" onClick={() => onToggleActive(m.model_id, m.is_active === false)} data-testid={`model-toggle-active-${m.model_id}`}>
                    {m.is_active === false ? t('walletCore.activate') : t('walletCore.deactivate')}
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="border-t border-border pt-4 mt-2">
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-heading font-semibold text-foreground" data-testid="mp-form-title">{editing ? t('walletCore.editModel') : t('walletCore.addModel')}</h4>
          {editing && <Button variant="ghost" size="sm" onClick={reset} data-testid="mp-cancel"><T k="walletCore.cancel" /></Button>}
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label={<T k="walletCore.provider" />}>
            <Input value={form.provider} onChange={(e) => setForm({ ...form, provider: e.target.value })} placeholder="anthropic" data-testid="mp-provider" />
          </Field>
          <Field label={<T k="walletCore.modelApiName" />}>
            <Input value={form.model_id} disabled={editing} onChange={(e) => setForm({ ...form, model_id: e.target.value })} placeholder="claude-sonnet-4-6" data-testid="mp-model-id" />
          </Field>
          <Field label={<T k="walletCore.displayLabel" />}>
            <Input value={form.display_label} onChange={(e) => setForm({ ...form, display_label: e.target.value })} data-testid="mp-display-label" />
          </Field>
          <Field label={<T k="walletCore.tierHint" />}>
            <Input value={form.tier_hint} onChange={(e) => setForm({ ...form, tier_hint: e.target.value })} placeholder="frontier" data-testid="mp-tier-hint" />
          </Field>
          <Field label={<T k="walletCore.chargeType" />}>
            <select
              value={form.charge_type}
              onChange={(e) => setForm({ ...form, charge_type: e.target.value })}
              className="w-full rounded-md border border-border px-3 py-2 text-sm bg-card"
              data-testid="mp-charge-type"
            >
              <option value="formula"><T k="walletCore.formula" /></option>
              <option value="flat"><T k="walletCore.flat" /></option>
            </select>
          </Field>
          <Field label={<T k="walletCore.selectable" />}>
            <Switch checked={!!form.user_selectable} onCheckedChange={(v) => setForm({ ...form, user_selectable: v })} data-testid="mp-user-selectable" />
          </Field>
          {isFlat ? (
            <Field label={<T k="walletCore.flatCredits" />}>
              <Input type="number" value={form.flat_credits} onChange={(e) => setForm({ ...form, flat_credits: e.target.value })} data-testid="mp-flat-credits" />
            </Field>
          ) : (
            <>
              <Field label={<T k="walletCore.usdInput" />}>
                <Input type="number" step="0.001" value={form.usd_price_per_1k_input} onChange={(e) => setForm({ ...form, usd_price_per_1k_input: e.target.value })} data-testid="mp-usd-input" />
              </Field>
              <Field label={<T k="walletCore.usdOutput" />}>
                <Input type="number" step="0.001" value={form.usd_price_per_1k_output} onChange={(e) => setForm({ ...form, usd_price_per_1k_output: e.target.value })} data-testid="mp-usd-output" />
              </Field>
            </>
          )}
        </div>
        <div className="mt-4">
          <Button onClick={submit} disabled={saving} data-testid="mp-save">
            {saving ? t('walletCore.mpSaving') : (editing ? t('walletCore.updateModel') : t('walletCore.addModel'))}
          </Button>
        </div>
      </div>
    </SectionCard>
  );
}

// Grant-by-email box: takes a user EMAIL (not a raw UUID) + amount + optional note. onGrant
// resolves email->user server-side and calls the existing wallet_grant. `user_not_found` shows
// inline (no submit); success shows the resolved email + new balance via the shared formatter.
// fixedEmail pins the card to one known user (the Manage popup): the email field disappears
// and the target cannot be retyped. Without it the card behaves as before — by email.
export function GrantCreditsCard({ peg = 0.01, onGrant, fixedEmail }) {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const target = (fixedEmail ?? email).trim();
  const canSubmit = !!target && Number(amount) > 0;

  const submit = async () => {
    if (!canSubmit) return;
    setError(''); setResult(null); setBusy(true);
    try {
      const r = await onGrant(target, Number(amount), note.trim());
      setResult(r); setEmail(''); setAmount(''); setNote('');
      toast.success(t('walletCore.granted'));
    } catch (e) {
      if (e?.message === 'user_not_found' || e?.status === 404) setError(t('walletCore.grantNotFound'));
      else toast.error(e.message);
    } finally { setBusy(false); }
  };

  return (
    <SectionCard title={t('walletCore.grant')} testId="wallet-grant-card">
      <div className="flex flex-wrap items-end gap-3">
        {!fixedEmail && (
        <Field label={<T k="walletCore.grantEmail" />}>
          <Input
            type="email"
            value={email}
            onChange={(e) => { setEmail(e.target.value); setError(''); }}
            placeholder={t('walletCore.grantEmailPlaceholder')}
            data-testid="grant-email"
            className="w-72"
          />
        </Field>
        )}
        <Field label={<T k="walletCore.grantAmount" />}>
          <Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="grant-amount" className="w-32" />
        </Field>
        <Field label={<T k="walletCore.grantNote" />}>
          <Input value={note} onChange={(e) => setNote(e.target.value)} data-testid="grant-note" className="w-56" />
        </Field>
        <Button onClick={submit} disabled={busy || !canSubmit} data-testid="grant-submit"><T k="walletCore.grantDo" /></Button>
      </div>
      {error && <p className="mt-3 text-sm text-destructive" data-testid="grant-error">{error}</p>}
      {result && (
        <p className="mt-3 text-sm text-success" data-testid="grant-success">
          {t('walletCore.grantedTo')} <span className="font-data">{result.email}</span> · {formatCredits(result.wallet?.balance_credits, peg)}
        </p>
      )}
    </SectionCard>
  );
}

// Container: fetches the dashboard + exposes super-admin grant / config / model-pricing controls.
// `show` (Ali, 2026-08-05): 'overview' renders only the financial overview (the credit
// dashboard tab), 'config' only the money form (its own tab). Unset renders both, as before.
export default function WalletDashboard({ show }) {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [cfg, setCfg] = useState(null);

  const load = useCallback(() => {
    api.tenantAdmin('wallet_dashboard').then(setData).catch((e) => toast.error(e.message));
    api.tenantAdmin('get_wallet_config').then((r) => setCfg(r.config)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const saveCfg = async () => {
    try { await api.tenantAdmin('set_wallet_config', cfg); toast.success(t('walletCore.configSaved')); load(); }
    catch (e) { toast.error(e.message); }
  };

  if (!data) return <Skeleton className="h-40 w-full" />;

  return (
    <div className="flex flex-col gap-6">
      {show !== 'config' && <WalletDashboardView data={data} />}

      {/* Granting credits to a user moved into the per-user Manage popup (Ali, 2026-08-03). */}

      {show !== 'overview' && cfg && (
        <SectionCard title={t('walletCore.configTitle')} testId="wallet-config-card">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <Field label={<T k="walletCore.peg" />}>
              <Input type="number" step="0.001" value={cfg.credit_usd_peg} onChange={(e) => setCfg({ ...cfg, credit_usd_peg: Number(e.target.value) })} data-testid="cfg-peg" />
            </Field>
            <Field label={<T k="walletCore.ragCharge" />}>
              <Input type="number" value={cfg.rag_charge_credits} onChange={(e) => setCfg({ ...cfg, rag_charge_credits: Number(e.target.value) })} data-testid="cfg-rag" />
            </Field>
            <Field label={<T k="walletCore.freeFlat" />}>
              <Input type="number" value={cfg.free_model_flat_credits} onChange={(e) => setCfg({ ...cfg, free_model_flat_credits: Number(e.target.value) })} data-testid="cfg-free" />
            </Field>
            <Field label={<T k="walletCore.markup" />}>
              <Input type="number" step="0.1" value={cfg.markup_default} onChange={(e) => setCfg({ ...cfg, markup_default: Number(e.target.value) })} data-testid="cfg-markup" />
            </Field>
            <Field label={<T k="walletCore.appMockFee" />}>
              <Input type="number" value={cfg.app_mock_flat_credits ?? 10} onChange={(e) => setCfg({ ...cfg, app_mock_flat_credits: Number(e.target.value) })} data-testid="cfg-app-mock-fee" />
            </Field>
            <Field label={<T k="walletCore.meteringEnabled" />}>
              <Switch checked={!!cfg.metering_enabled} onCheckedChange={(v) => setCfg({ ...cfg, metering_enabled: v })} data-testid="cfg-metering" />
            </Field>
          </div>
          <div className="mt-4"><Button onClick={saveCfg} data-testid="cfg-save"><T k="walletCore.saveConfig" /></Button></div>
        </SectionCard>
      )}
    </div>
  );
}

